import Attachment16 from '@carbon/icons-react/es/attachment/16';
import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import CaretUp16 from '@carbon/icons-react/es/caret--up/16';
import Download16 from '@carbon/icons-react/es/download/16';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { StatusUpdateId } from 'api/models';
import {
  StatusUpdate,
  StatusUpdatesAPI,
  StatusUpdateState,
} from 'api/status-updates';
import { Collapse, Panel, PanelHeaderTitle } from 'components/atoms/collapse';
import { IconShell } from 'components/atoms/icon-shell';
import {
  Attachments,
  AttachmentsTabViews,
} from 'components/molecules/attachments';
import { DownloadLink } from 'components/molecules/download-link/download-link';
import { toastService } from 'components/molecules/toast';
import { selectActiveStatusUpdate } from 'data/status-updates';
import { useTranslation } from 'hooks/use-translation';
import { useAttachStatusUpdateFileMutation } from 'queries/attachments';
import {
  getStatusUpdatesDetailQueryKey,
  useGetStatusUpdateDetailsQuery,
} from 'queries/status-updates';
import { StyledButtonWrapper, SUAttachmentWrapper } from './styles';

interface Props {
  activeStatusUpdate: StatusUpdate;
  attachmentKey: string | undefined;
  setAttachmentKey: (state) => void;
  trigger: number | undefined;
}

const HeaderWrapper = styled.div``;

const PreviewAttachment = styled.div<{ canShowPane: boolean }>`
  ${({ canShowPane }) =>
    canShowPane
      ? css`
          display: block;
        `
      : css`
          display: none;
        `};
`;

const StyledCollapse = styled(Collapse)`
  border-top: 1px solid var(--border-color);
  .ant-collapse-item {
    padding-bottom: 0 !important;
  }
  .ant-collapse-item.ant-collapse-item-active {
    padding-bottom: 0 !important;
  }
`;

const SUAttachmentComp: FC<Props> = ({
  activeStatusUpdate,
  attachmentKey,
  setAttachmentKey,
  trigger,
}) => {
  const [view, setView] = useState(AttachmentsTabViews.GridView);
  const { data: statusUpdateDetails } = useGetStatusUpdateDetailsQuery(
    StatusUpdateId(activeStatusUpdate.statusUpdateId),
  );

  const queryClient = useQueryClient();

  const { mutateAsync: addSUAttachment } = useAttachStatusUpdateFileMutation(
    activeStatusUpdate.statusUpdateId,
    activeStatusUpdate.project.projectId,
  );

  const message = useTranslation('status_updates.attachments.error.title');
  const description = useTranslation(
    'status_updates.attachments.error.description',
  );
  const toast = toastService({
    title: message,
    message: description,
  });

  const onUploadAttachment = useCallback(
    (file: File) => {
      return addSUAttachment(
        { file },
        {
          onSuccess: () =>
            queryClient.refetchQueries(
              getStatusUpdatesDetailQueryKey(
                StatusUpdateId(activeStatusUpdate.statusUpdateId),
              ),
            ),
          onError: () => {
            toast.error();
          },
        },
      );
    },
    [activeStatusUpdate.statusUpdateId, addSUAttachment, queryClient, toast],
  );

  const downloadLink = (
    <DownloadLink
      url={StatusUpdatesAPI.downloadStatusUpdateAttachmentsUrl(
        activeStatusUpdate.statusUpdateId,
      )}
    >
      <StyledButtonWrapper>
        <Download16 />
      </StyledButtonWrapper>
    </DownloadLink>
  );

  const isPublished = activeStatusUpdate.state === StatusUpdateState.Published;

  // trigger is intentionally used to re-run the use-Effect in Attachments list to open the file upload manager
  const openFileDrawer = useCallback(
    (uploadBtnRef) => {
      if (trigger) {
        uploadBtnRef.current?.click();
      }
    },

    [trigger],
  );

  const onChange = useCallback(
    (key) => {
      setAttachmentKey(key);
    },
    [setAttachmentKey],
  );

  return (
    <PreviewAttachment
      canShowPane={!!(statusUpdateDetails?.attachments ?? []).length}
    >
      <StyledCollapse
        expandIconPosition="right"
        expandIcon={({ isActive }) => {
          return isActive ? <CaretDown16 /> : <CaretUp16 />;
        }}
        onChange={onChange}
        activeKey={attachmentKey}
        ghost
      >
        <Panel
          key="attachments"
          header={
            <HeaderWrapper>
              <PanelHeaderTitle
                icon={<IconShell icon={<Attachment16 />} />}
                title={<FormattedMessage id="attachments" />}
              />
            </HeaderWrapper>
          }
          extra={
            statusUpdateDetails?.attachments &&
            statusUpdateDetails.attachments?.length > 0 && (
              <Attachments.Tabs
                onChooseTab={setView}
                downloadLink={downloadLink}
              />
            )
          }
        >
          <Attachments.List
            descriptionLabel={<FormattedMessage id="su.attachments.label" />}
            view={view}
            data={statusUpdateDetails?.attachments || []}
            onUploadRequest={onUploadAttachment}
            readonly={isPublished}
            externalTrigger={openFileDrawer}
            externalTriggerEnabled
            isBasicPreview
          />
        </Panel>
      </StyledCollapse>
    </PreviewAttachment>
  );
};

export const SUAttachment = ({ attachmentKey, setAttachmentKey, trigger }) => {
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);

  if (!activeStatusUpdate) {
    return null;
  }

  return (
    <SUAttachmentWrapper>
      <SUAttachmentComp
        attachmentKey={attachmentKey}
        setAttachmentKey={setAttachmentKey}
        activeStatusUpdate={activeStatusUpdate}
        trigger={trigger}
      />
    </SUAttachmentWrapper>
  );
};
